// Contexto do workspace por domínio: estado, (de)serialização na URL (?ctx=)
// e matcher host↔scope (espelha _host_match_statement do backend).

export type WafContext =
  | { kind: 'none' }
  | { kind: 'resource'; arn: string; name: string; hosts: string[] }
  | { kind: 'host'; host: string; resourceArn: string }

export function parseWafContext(raw: string | null | undefined): WafContext {
  const v = (raw ?? '').trim()
  if (v.startsWith('host:')) {
    return { kind: 'host', host: decodeURIComponent(v.slice(5)), resourceArn: '' }
  }
  if (v.startsWith('resource:')) {
    return { kind: 'resource', arn: decodeURIComponent(v.slice(9)), name: '', hosts: [] }
  }
  return { kind: 'none' }
}

export function serializeWafContext(ctx: WafContext): string {
  if (ctx.kind === 'host') return `host:${encodeURIComponent(ctx.host)}`
  if (ctx.kind === 'resource') return `resource:${encodeURIComponent(ctx.arn)}`
  return ''
}

// host casa um scoped host: igualdade case-insensitive OU curinga '*.x'
// (subdomínios de x, espelhando ENDS_WITH '.x' do backend).
export function hostMatchesScope(host: string, scope: string): boolean {
  const h = (host || '').trim().toLowerCase()
  const s = (scope || '').trim().toLowerCase()
  if (!h || !s) return false
  if (s.startsWith('*.')) return h.endsWith(s.slice(1)) && h !== s.slice(2)
  return h === s
}

function contextHosts(ctx: WafContext): string[] {
  if (ctx.kind === 'host') return [ctx.host]
  if (ctx.kind === 'resource') return ctx.hosts
  return []
}

// Regra pertence ao contexto se algum scoped_hosts casa algum host do contexto.
export function ruleInContext(
  rule: { scoped_hosts?: string[] | null }, ctx: WafContext,
): boolean {
  const ch = contextHosts(ctx)
  const sh = rule.scoped_hosts ?? []
  if (!ch.length || !sh.length) return false
  return sh.some(s => ch.some(h => hostMatchesScope(h, s)))
}

// scoped_hosts da regra que NÃO são cobertos pelos hosts do contexto
// (→ "também afeta outros domínios").
export function scopeExceedsContext(
  rule: { scoped_hosts?: string[] | null }, ctx: WafContext,
): boolean {
  const ch = contextHosts(ctx)
  const sh = rule.scoped_hosts ?? []
  return sh.some(s => !ch.some(h => hostMatchesScope(h, s)))
}

// Lista dos scoped_hosts da regra que NÃO são cobertos por nenhum host do
// contexto (os domínios "extra" que a alteração também afetaria). Sem contexto
// ativo, retorna todos os scoped_hosts.
export function hostsOutsideContext(
  rule: { scoped_hosts?: string[] | null }, ctx: WafContext,
): string[] {
  const ch = contextHosts(ctx)
  const sh = rule.scoped_hosts ?? []
  return sh.filter(s => !ch.some(h => hostMatchesScope(h, s)))
}
